import type { Settings as SettingsValue } from '@frisk/shared';
import { isUploadedFeed, labelFor } from '@frisk/shared';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { BringConnect } from '../components/BringConnect';
import { WeatherConnect } from '../components/WeatherConnect';
import { WasteIcon } from '../components/widgets/WasteIcon';
import { ImportFeed } from '../components/ImportFeed';
import { kicker, memberColor, screenTitle, t } from '../theme';
import { useStore } from '../store';

type ToggleKey = 'calendarSync' | 'voiceCapture' | 'childLock' | 'nightDim';

interface ToggleSpec {
  key: ToggleKey;
  label: string;
  hint: string;
  /** Set when the switch would promise something the app cannot do yet. */
  unavailable?: string;
}

const card: React.CSSProperties = {
  borderRadius: 24,
  background: t.surface,
  border: t.hairline,
  padding: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const cardKicker: React.CSSProperties = {
  fontFamily: t.mono,
  fontSize: 12,
  letterSpacing: '0.14em',
  color: t.inkFaint,
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 16px',
  borderRadius: 16,
  background: 'oklch(0.26 0.012 60)',
};

export function Settings() {
  const members = useStore((s) => s.members);
  const categories = useStore((s) => s.categories);
  const settings = useStore((s) => s.settings);
  const feeds = useStore((s) => s.feeds);
  const sttAvailable = useStore((s) => s.sttAvailable);

  const username = useStore((s) => s.username);
  const { patchSettings, patchCategory, addMember, renameMember, removeMember, addFeed, removeFeed, syncFeed, setFeedMember, setFeedRole, changePassword, logout, flash } =
    useStore.getState();

  const [bring, setBring] = useState<Awaited<ReturnType<typeof api.bringStatus>> | null>(null);
  const [connecting, setConnecting] = useState(false);
  const loadBring = () => void api.bringStatus().then(setBring).catch(() => {});
  useEffect(loadBring, []);

  const weather = useStore((s) => s.weather);
  const refresh = useStore((s) => s.refresh);
  const [pickingPlace, setPickingPlace] = useState(false);
  const [importing, setImporting] = useState(false);
  const reimport = useRef<HTMLInputElement>(null);
  const [reimportId, setReimportId] = useState<string | null>(null);
  const polled = feeds.filter((f) => !isUploadedFeed(f));
  const rules = useStore((s) => s.rules);
  const updateRule = useStore((s) => s.updateRule);
  const clearRule = useStore((s) => s.clearRule);
  const loadWeather = () => void refresh('weather');

  const [devices, setDevices] = useState<
    Array<{ id: string; userAgent: string | null; lastSeenAt: string | null; current: boolean }>
  >([]);

  const loadDevices = () => void api.listSessions().then(setDevices).catch(() => {});
  useEffect(loadDevices, []);

  /** Chrome on iPad -> "iPad · Safari" is all anyone needs to recognise it. */
  const describeDevice = (ua: string | null) => {
    if (!ua) return 'Unbekanntes Gerät';
    const platform = /iPad/.test(ua) ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Macintosh/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Gerät';
    const browser = /CriOS|Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser';
    return `${platform} · ${browser}`;
  };

  const relativeSync = (iso: string | null) => {
    if (!iso) return 'noch nie';
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return 'gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;
    const hours = Math.round(minutes / 60);
    return hours < 24 ? `vor ${hours} Std.` : `vor ${Math.round(hours / 24)} Tagen`;
  };

  const toggles: ToggleSpec[] = [
    {
      key: 'calendarSync',
      label: 'Kalender-Sync',
      hint: feeds.length
        // Only URL feeds are polled; counting imported files here would
        // promise an automatic refresh that cannot happen.
        ? `${polled.length} Abo${polled.length === 1 ? '' : 's'}, automatisch alle 15 Min.${
            feeds.length > polled.length
              ? ` · ${feeds.length - polled.length} importiert`
              : ''
          }`
        : 'Erst einen Kalender abonnieren (unten)',
      unavailable: feeds.length === 0 ? 'Noch kein Kalender abonniert' : undefined,
    },
    {
      key: 'voiceCapture',
      label: 'Sprach-Erfassung',
      hint: 'Ablaufdatum beim Einräumen diktieren',
      unavailable: sttAvailable ? undefined : 'Kein Whisper-Dienst konfiguriert',
    },
    { key: 'childLock', label: 'Kindermodus', hint: 'Löschen nur mit Bestätigung' },
    { key: 'nightDim', label: 'Nachtabsenkung', hint: 'Display dunkelt ab 22 Uhr ab' },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '34px 38px 0' }}>
      <div style={{ flex: 'none' }}>
        <div style={kicker}>HAUSHALT &amp; SYSTEM</div>
        <div style={{ ...screenTitle, whiteSpace: 'nowrap' }}>Einstellungen</div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          marginTop: 24,
          paddingBottom: 30,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: 16,
          alignContent: 'start',
        }}
      >
        {/* --- members ------------------------------------------------ */}
        <div style={card}>
          <div style={cardKicker}>MITGLIEDER</div>
          {members.map((m) => (
            <div key={m.id} style={row}>
              <div style={{ width: 20, height: 20, flex: 'none', borderRadius: 999, background: memberColor(m.hue) }} />
              <div
                className="press"
                onClick={() => renameMember(m.id, m.name)}
                style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <div style={{ fontSize: 22, fontWeight: 500, minWidth: 0, hyphens: 'auto', overflowWrap: 'break-word' }}>{m.name}</div>
                <div style={{ flex: 1 }} />
                <div style={{ fontSize: 17, color: 'oklch(0.66 0.015 70)', whiteSpace: 'nowrap' }}>umbenennen</div>
              </div>
              <div
                className="press"
                onClick={() => void removeMember(m.id)}
                style={{
                  width: 44,
                  height: 44,
                  flex: 'none',
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  color: 'oklch(0.68 0.05 30)',
                }}
              >
                ×
              </div>
            </div>
          ))}
          <div
            className="press"
            onClick={addMember}
            style={{
              marginTop: 4,
              height: 64,
              borderRadius: 16,
              border: '1px dashed oklch(1 0 0 / 0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 500,
              color: 'oklch(0.8 0.015 70)',
            }}
          >
            Mitglied hinzufügen
          </div>
        </div>

        {/* --- shelf-life defaults ------------------------------------ */}
        <div style={card}>
          <div style={cardKicker}>HALTBARKEIT — VORSCHLÄGE</div>
          <div style={{ fontSize: 16, color: t.inkMuted, marginTop: -4, textWrap: 'pretty' }}>
            Gilt für neu erfasste Artikel. Schon eingeräumte behalten ihre Haltbarkeit.
          </div>
          {categories.map((c) => (
            <div key={c.id} style={row}>
              <div style={{ width: 16, height: 16, flex: 'none', borderRadius: 999, background: memberColor(c.hue) }} />
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 500,
                  minWidth: 0,
                  // German compounds need hyphens, not an arbitrary letter break.
                  hyphens: 'auto',
                  overflowWrap: 'break-word',
                }}
              >
                {c.label}
              </div>
              <div style={{ flex: 1, minWidth: 8 }} />
              <div
                className="press"
                onClick={() => void patchCategory(c.id, Math.max(1, c.defaultShelfDays - 1))}
                style={{ width: 42, height: 42, flex: 'none', borderRadius: 999, background: t.controlHi, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}
              >
                –
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, minWidth: 74, flex: 'none', textAlign: 'center', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {labelFor(c.defaultShelfDays)}
              </div>
              <div
                className="press"
                onClick={() => void patchCategory(c.id, Math.min(3650, c.defaultShelfDays + 1))}
                style={{ width: 42, height: 42, flex: 'none', borderRadius: 999, background: t.controlHi, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}
              >
                +
              </div>
            </div>
          ))}
        </div>

        {/* --- system -------------------------------------------------- */}
        <div style={card}>
          <div style={cardKicker}>SYSTEM</div>
          {toggles.map((spec) => {
            const on = settings[spec.key];
            const blocked = Boolean(spec.unavailable);
            return (
              <div
                key={spec.key}
                className={blocked ? undefined : 'press'}
                onClick={() => !blocked && void patchSettings({ [spec.key]: !on } as Partial<SettingsValue>)}
                style={{ ...row, opacity: blocked ? 0.55 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 21, fontWeight: 500 }}>{spec.label}</div>
                  <div style={{ fontSize: 16, color: 'oklch(0.66 0.015 70)', marginTop: 2, textWrap: 'pretty' }}>
                    {spec.unavailable ?? spec.hint}
                  </div>
                </div>
                <div style={{ flex: 1 }} />
                <div
                  style={{
                    width: 68,
                    height: 38,
                    flex: 'none',
                    borderRadius: 999,
                    background: on && !blocked ? t.accent : 'oklch(0.36 0.012 60)',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 4,
                    transition: 'background 140ms',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      background: on && !blocked ? 'oklch(0.2 0.05 130)' : 'oklch(0.62 0.012 60)',
                      transform: on ? 'translateX(30px)' : 'none',
                      transition: 'transform 140ms',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* --- calendar subscriptions ---------------------------------- */}
        <div style={{ ...card, gridColumn: '1 / -1' }}>
          <div style={cardKicker}>KALENDER-ABOS (NUR LESEN)</div>
          <div style={{ fontSize: 16, color: t.inkMuted, marginTop: -4, textWrap: 'pretty' }}>
            Geheime iCal-Adresse aus Google oder iCloud. Einbahnstraße — hier angelegte Termine werden
            nie hochgeladen.
          </div>

          {feeds.map((f) => {
            return (
              <div key={f.id} style={{ ...row, flexWrap: 'wrap', rowGap: 10 }}>
                <div style={{ minWidth: 200, flex: 1 }}>
                  <div style={{ fontSize: 21, fontWeight: 500 }}>{f.label}</div>
                  <div style={{ fontSize: 15, color: f.lastError ? 'oklch(0.78 0.14 30)' : 'oklch(0.66 0.015 70)', marginTop: 2, textWrap: 'pretty' }}>
                    {f.lastError ??
                      `${f.lastEventCount ?? 0} Termine · ${isUploadedFeed(f) ? 'importiert' : ''} ${relativeSync(f.lastSyncedAt)}`}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Re-tag in place: a bin calendar subscribed as a normal one
                      is an easy mistake and should not need deleting to fix. */}
                  <div
                    className="press"
                    onClick={() => void setFeedRole(f.id, f.role === 'waste' ? 'family' : 'waste')}
                    title={f.role === 'waste' ? 'Als normalen Kalender behandeln' : 'Als Müllkalender behandeln'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      padding: '9px 14px',
                      borderRadius: 999,
                      fontSize: 16,
                      fontWeight: 500,
                      background: f.role === 'waste' ? 'oklch(0.3 0.012 60)' : 'transparent',
                      border: f.role === 'waste' ? '1px solid oklch(0.70 0.02 70)' : t.hairline,
                      color: f.role === 'waste' ? t.ink : 'oklch(0.6 0.015 70)',
                    }}
                  >
                    <WasteIcon size={17} />
                    Müll
                  </div>
                  <div style={{ width: 1, height: 26, background: 'oklch(1 0 0 / 0.1)', margin: '0 2px' }} />
                  <div
                    className="press"
                    onClick={() => void setFeedMember(f.id, null)}
                    style={{
                      padding: '9px 14px',
                      borderRadius: 999,
                      fontSize: 16,
                      background: f.memberId === null ? 'oklch(0.34 0.012 60)' : 'transparent',
                      border: f.memberId === null ? `1px solid ${t.accent}` : t.hairline,
                    }}
                  >
                    Alle
                  </div>
                  {members.map((m) => (
                    <div
                      key={m.id}
                      className="press"
                      onClick={() => void setFeedMember(f.id, m.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                        padding: '9px 14px',
                        borderRadius: 999,
                        fontSize: 16,
                        background: f.memberId === m.id ? 'oklch(0.34 0.012 60)' : 'transparent',
                        border: f.memberId === m.id ? `1px solid ${memberColor(m.hue)}` : t.hairline,
                        color: f.memberId === m.id ? t.ink : 'oklch(0.7 0.015 70)',
                      }}
                    >
                      <div style={{ width: 10, height: 10, borderRadius: 999, background: memberColor(m.hue) }} />
                      {m.name}
                    </div>
                  ))}
                </div>

                {/* An imported file has no URL behind it, so "Jetzt laden"
                    would have nothing to load — it offers the next file. */}
                <div
                  className="press"
                  onClick={() => {
                    if (isUploadedFeed(f)) {
                      setReimportId(f.id);
                      reimport.current?.click();
                    } else {
                      void syncFeed(f.id);
                    }
                  }}
                  style={{ height: 44, padding: '0 16px', borderRadius: 12, background: t.controlHi, display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 500, whiteSpace: 'nowrap' }}
                >
                  {isUploadedFeed(f) ? 'Neue Datei' : 'Jetzt laden'}
                </div>
                <div
                  className="press"
                  onClick={() => void removeFeed(f.id)}
                  style={{ width: 44, height: 44, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: 'oklch(0.68 0.05 30)' }}
                >
                  ×
                </div>
              </div>
            );
          })}

          {/* Two buttons rather than a third prompt step: which kind of
              calendar this is, is known before the URL is pasted. */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <div
              className="press"
              onClick={() => addFeed('waste')}
              style={{
                flex: 1,
                height: 64,
                borderRadius: 16,
                border: '1px dashed oklch(1 0 0 / 0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontSize: 20,
                fontWeight: 500,
                color: 'oklch(0.8 0.015 70)',
              }}
            >
              <WasteIcon size={22} />
              Müllkalender
            </div>
            <div
              className="press"
              onClick={() => setImporting(true)}
              title="Heruntergeladene .ics-Datei importieren"
              style={{
                flex: 1,
                height: 64,
                borderRadius: 16,
                border: '1px dashed oklch(1 0 0 / 0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 500,
                color: 'oklch(0.8 0.015 70)',
              }}
            >
              ICS-Datei importieren
            </div>
          <div
            className="press"
            onClick={() => addFeed('family')}
            style={{
              flex: 1,
              height: 64,
              borderRadius: 16,
              border: '1px dashed oklch(1 0 0 / 0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 500,
              color: 'oklch(0.8 0.015 70)',
            }}
          >
            Kalender abonnieren
          </div>
          </div>
        </div>

        {/* --- Nachkauf-Regeln ------------------------------------------ */}
        <div style={card}>
          <div style={cardKicker}>IMMER VORRÄTIG</div>
          <div style={{ fontSize: 16, color: t.inkMuted, marginTop: -4, textWrap: 'pretty' }}>
            Läuft der Bestand unter die Zahl, wandert der Artikel automatisch auf die
            Einkaufsliste. Angelegt wird eine Regel direkt am Artikel im Inhalt.
          </div>

          {rules.length === 0 ? (
            <div style={{ fontSize: 16, color: t.inkFaint, textWrap: 'pretty' }}>
              Noch keine Regel. Tippe im Inhalt auf einen Artikel und schalte „Immer vorrätig" ein.
            </div>
          ) : (
            rules.map((r) => {
              /*
               * "0 von 2 vorrätig" is the honest signal that a rule matches
               * nothing — a rule whose name never lines up with any product
               * fails silently, and you would only find out when the milk is
               * gone. Showing the live count makes that visible on sight.
               */
              return (
                <div key={r.id} style={{ ...row, justifyContent: 'space-between' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 21, fontWeight: 500 }}>{r.label}</div>
                    <div style={{ fontSize: 15, color: t.inkFaint, marginTop: 2, textWrap: 'pretty' }}>
                      {r.stock} von {r.minQty} vorrätig ·{' '}
                      {r.armed ? 'wird überwacht' : 'steht auf der Liste'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                    <div className="press" onClick={() => void updateRule(r.id, Math.max(1, r.minQty - 1))} style={ruleStep}>
                      −
                    </div>
                    <div style={{ minWidth: 44, textAlign: 'center', fontSize: 20, fontWeight: 600 }}>{r.minQty}×</div>
                    <div className="press" onClick={() => void updateRule(r.id, Math.min(99, r.minQty + 1))} style={ruleStep}>
                      +
                    </div>
                    <div
                      className="press"
                      onClick={() => void clearRule(r.id)}
                      style={{ width: 44, height: 44, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: 'oklch(0.68 0.05 30)' }}
                    >
                      ×
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* --- Wetter --------------------------------------------------- */}
        <div style={card}>
          <div style={cardKicker}>WETTER</div>

          {weather?.configured && weather.place ? (
            <>
              <div style={{ ...row, justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 21, fontWeight: 500 }}>{weather.place.name}</div>
                  <div
                    style={{
                      fontSize: 15,
                      color: weather.lastError ? 'oklch(0.78 0.14 30)' : 'oklch(0.66 0.015 70)',
                      marginTop: 2,
                      textWrap: 'pretty',
                    }}
                  >
                    {weather.lastError ?? `${weather.place.region} · ${relativeSync(weather.fetchedAt)}`}
                  </div>
                </div>
                <div
                  className="press"
                  onClick={() => setPickingPlace(true)}
                  style={{ height: 44, padding: '0 16px', borderRadius: 12, background: t.controlHi, display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 500, whiteSpace: 'nowrap' }}
                >
                  Ort ändern
                </div>
              </div>
              <div style={{ fontSize: 15, color: t.inkFaint, textWrap: 'pretty' }}>
                Open-Meteo, ohne Konto und ohne Schlüssel. Der Server fragt alle 20 Minuten ab und
                zeigt bei Ausfall weiter den letzten Stand — mit Uhrzeit, damit er nicht für aktuell
                gehalten wird.
              </div>
              <div
                className="press"
                onClick={async () => {
                  await api.weatherDisconnect().catch(() => {});
                  loadWeather();
                  flash('Wetter entfernt');
                }}
                style={{ marginTop: 4, height: 60, borderRadius: 16, border: '1px solid oklch(0.72 0.17 25 / 0.4)', color: 'oklch(0.82 0.14 30)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 500 }}
              >
                Entfernen
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 16, color: t.inkMuted, textWrap: 'pretty' }}>
                Zeigt Temperatur und den nächsten Wetterwechsel auf der Übersicht. Kein Konto nötig —
                es reicht, einen Ort zu wählen.
              </div>
              <div
                className="press"
                onClick={() => setPickingPlace(true)}
                style={{ marginTop: 4, height: 64, borderRadius: 16, border: '1px dashed oklch(1 0 0 / 0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}
              >
                Ort wählen
              </div>
            </>
          )}
        </div>

        {/* --- Bring ---------------------------------------------------- */}
        <div style={card}>
          <div style={cardKicker}>EINKAUFSLISTE — BRING!</div>

          {!bring?.available ? (
            <div style={{ fontSize: 17, color: t.inkMuted, textWrap: 'pretty' }}>
              Nicht verfügbar: INTEGRATION_KEY ist nicht gesetzt. Ohne den Schlüssel speichert frisk
              keine fremden Zugangsdaten, statt sie im Klartext abzulegen.
            </div>
          ) : bring.connected ? (
            <>
              <div style={{ ...row, justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 21, fontWeight: 500 }}>{bring.listName}</div>
                  <div style={{ fontSize: 15, color: bring.lastError ? 'oklch(0.78 0.14 30)' : 'oklch(0.66 0.015 70)', marginTop: 2, textWrap: 'pretty' }}>
                    {bring.lastError ?? `${bring.itemCount} Einträge · ${relativeSync(bring.lastSyncedAt)}`}
                  </div>
                </div>
                <div
                  className="press"
                  onClick={async () => {
                    await api.bringSync().catch(() => {});
                    loadBring();
                    flash('Bring aktualisiert');
                  }}
                  style={{ height: 44, padding: '0 16px', borderRadius: 12, background: t.controlHi, display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 500, whiteSpace: 'nowrap' }}
                >
                  Jetzt laden
                </div>
              </div>
              <div style={{ fontSize: 15, color: t.inkFaint, textWrap: 'pretty' }}>
                Bring führt die Liste. Abhaken in frisk hakt in Bring ab — der Eintrag verschwindet,
                wie in der Bring-App.
              </div>
              <div
                className="press"
                onClick={async () => {
                  await api.bringDisconnect().catch(() => {});
                  loadBring();
                  flash('Bring getrennt — Liste bleibt lokal erhalten');
                }}
                style={{ marginTop: 4, height: 60, borderRadius: 16, border: '1px solid oklch(0.72 0.17 25 / 0.4)', color: 'oklch(0.82 0.14 30)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, fontWeight: 500 }}
              >
                Trennen
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 16, color: t.inkMuted, textWrap: 'pretty' }}>
                Bring übernimmt die Einkaufsliste. frisk spiegelt sie nur — dadurch gibt es keine
                zwei Listen, die auseinanderlaufen können.
              </div>
              <div
                className="press"
                onClick={() => setConnecting(true)}
                style={{ marginTop: 4, height: 64, borderRadius: 16, border: '1px dashed oklch(1 0 0 / 0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}
              >
                Bring verbinden
              </div>
            </>
          )}
        </div>

        {/* --- account & devices --------------------------------------- */}
        <div style={card}>
          <div style={cardKicker}>KONTO &amp; GERÄTE</div>

          <div style={{ ...row, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 21, fontWeight: 500 }}>{username || '—'}</span>
            <div className="press" onClick={changePassword} style={{ fontSize: 17, color: t.inkMuted }}>
              Passwort ändern
            </div>
          </div>

          {devices.map((d) => (
            <div key={d.id} style={row}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 500 }}>
                  {describeDevice(d.userAgent)}
                  {d.current && <span style={{ color: t.accent, fontSize: 16 }}> · dieses Gerät</span>}
                </div>
                <div style={{ fontSize: 15, color: 'oklch(0.66 0.015 70)', marginTop: 2 }}>
                  {d.lastSeenAt ? `zuletzt ${new Date(d.lastSeenAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}` : 'noch nie'}
                </div>
              </div>
              {!d.current && (
                <div
                  className="press"
                  onClick={async () => {
                    await api.revokeSession(d.id);
                    loadDevices();
                    flash('Gerät abgemeldet');
                  }}
                  style={{ height: 44, padding: '0 16px', borderRadius: 12, background: t.controlHi, display: 'flex', alignItems: 'center', fontSize: 17, fontWeight: 500, whiteSpace: 'nowrap' }}
                >
                  Abmelden
                </div>
              )}
            </div>
          ))}

          <div
            className="press"
            onClick={() => void logout()}
            style={{
              marginTop: 4,
              height: 64,
              borderRadius: 16,
              border: '1px solid oklch(0.72 0.17 25 / 0.4)',
              color: 'oklch(0.82 0.14 30)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 600,
            }}
          >
            Auf diesem Gerät abmelden
          </div>
        </div>

        {/* --- about --------------------------------------------------- */}
        <div style={card}>
          <div style={cardKicker}>PANEL</div>
          <div style={{ ...row, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 20, fontWeight: 500 }}>Zeitzone</span>
            <span style={{ fontSize: 19, color: t.inkMuted }}>{settings.timeZone}</span>
          </div>
          <div style={{ ...row, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 20, fontWeight: 500 }}>Spracherkennung</span>
            <span style={{ fontSize: 19, color: sttAvailable ? t.accent : t.inkMuted }}>
              {sttAvailable ? 'verfügbar' : 'nicht konfiguriert'}
            </span>
          </div>
          <div
            className="press"
            onClick={() => void patchSettings({ setupComplete: false })}
            style={{
              marginTop: 4,
              height: 64,
              borderRadius: 16,
              background: t.controlHi,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 600,
            }}
          >
            Ersteinrichtung öffnen
          </div>
        </div>
      </div>
      {/* One hidden picker, reused for whichever feed is being replaced. */}
      <input
        ref={reimport}
        type="file"
        accept=".ics,text/calendar"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          const id = reimportId;
          e.target.value = '';
          if (!file || !id) return;
          try {
            const res = await api.reimportFeed(id, await file.text());
            await refresh('settings');
            await refresh('events');
            flash(res.sync.error ?? `${res.sync.count} Termine importiert`);
          } catch (err) {
            flash(err instanceof Error ? err.message : 'Import fehlgeschlagen');
          }
        }}
        style={{ display: 'none' }}
      />
      {importing && (
        <ImportFeed
          onClose={() => setImporting(false)}
          onDone={() => {
            void refresh('settings');
            void refresh('events');
          }}
        />
      )}
      {pickingPlace && (
        <WeatherConnect onClose={() => setPickingPlace(false)} onChosen={loadWeather} />
      )}
      {connecting && (
        <BringConnect onClose={() => setConnecting(false)} onConnected={loadBring} />
      )}
    </div>
  );
}

const ruleStep: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  background: t.control,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  fontWeight: 500,
};
